import { useEffect, useMemo, useState } from 'react';
import { Loader2, FileDown, X } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

const TERMS_OPTIONS = ['Net 30', 'Net 15', 'COD', 'Prepaid'];

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });

/**
 * Admin-only modal to spin an approved order into a branded invoice PDF.
 * The remit-to details default from the singleton business settings but
 * are editable per invoice so Priscila can tweak without opening a
 * separate settings tab. Ticking "Save as defaults" persists the choices.
 */
export default function InvoiceDialog({ order, open, onOpenChange, onCreated }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const [terms, setTerms] = useState('Net 30');
  const [taxRate, setTaxRate] = useState('0');
  const [shipping, setShipping] = useState('0');
  const [discount, setDiscount] = useState('0');
  const [notes, setNotes] = useState('');
  const [saveDefaults, setSaveDefaults] = useState(false);

  // Remit toggles + editable details.
  const [zelleOn, setZelleOn] = useState(true);
  const [zelleDetail, setZelleDetail] = useState('');
  const [achOn, setAchOn] = useState(false);
  const [achDetail, setAchDetail] = useState('');
  const [checkOn, setCheckOn] = useState(false);
  const [checkDetail, setCheckDetail] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/admin/business-settings')
      .then(({ data }) => {
        setSettings(data);
        setTerms(data.default_terms || 'Net 30');
        setZelleDetail(data.remit_zelle_email || '');
        setAchDetail(data.remit_ach_details || '');
        setCheckDetail(data.remit_check_address || '');
        setZelleOn(Boolean(data.remit_zelle_email));
        setAchOn(Boolean(data.remit_ach_details));
        setCheckOn(Boolean(data.remit_check_address));
        setTaxRate('0');
        setShipping('0');
        setDiscount('0');
        setNotes('');
        setSaveDefaults(false);
      })
      .catch(() => toast.error('Could not load business settings'))
      .finally(() => setLoading(false));
  }, [open]);

  // Preview total from the current order.
  const preview = useMemo(() => {
    const items = order?.items || [];
    const sub = items.reduce((s, it) => s + Number(it.line_total || 0), 0);
    const tax = Math.max(0, Number(taxRate) || 0);
    const ship = Math.max(0, Number(shipping) || 0);
    const disc = Math.max(0, Number(discount) || 0);
    const taxAmt = +(sub * tax / 100).toFixed(2);
    const total = +(sub + taxAmt + ship - disc).toFixed(2);
    return { sub, taxAmt, ship, disc, total };
  }, [order, taxRate, shipping, discount]);

  const submit = async () => {
    const remit_methods = [];
    if (zelleOn && zelleDetail.trim()) remit_methods.push({ kind: 'zelle', detail: zelleDetail.trim() });
    if (achOn && achDetail.trim()) remit_methods.push({ kind: 'ach', detail: achDetail.trim() });
    if (checkOn && checkDetail.trim()) remit_methods.push({ kind: 'check', detail: checkDetail.trim() });
    if (!remit_methods.length) {
      if (!window.confirm("No remit-to method selected. Send an invoice without payment instructions?")) return;
    }
    setBusy(true);
    try {
      const payload = {
        terms,
        tax_rate: Math.max(0, Number(taxRate) || 0),
        shipping_fee: Math.max(0, Number(shipping) || 0),
        discount: Math.max(0, Number(discount) || 0),
        remit_methods,
        notes,
        save_as_defaults: saveDefaults,
      };
      const { data: inv } = await api.post(`/admin/orders/${order.id}/invoice`, payload);
      toast.success(`Invoice ${inv.invoice_number} created`);
      // Immediately download the branded PDF.
      const resp = await api.get(`/admin/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      onCreated?.(inv);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create invoice');
    } finally { setBusy(false); }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-16px)] max-w-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6 gap-3 sm:gap-4 !grid-cols-[minmax(0,1fr)]"
        data-testid="invoice-dialog"
      >
        <DialogHeader>
          <DialogTitle className="serif text-xl sm:text-2xl">Generate invoice</DialogTitle>
          <p className="sans text-[10px] sm:text-xs tracking-widest uppercase text-neutral-500">
            From order {order.order_number} · {fmt(order.total)}
          </p>
        </DialogHeader>

        {loading ? (
          <p className="sans text-sm text-neutral-500 py-6">Loading defaults…</p>
        ) : (
          <div className="space-y-5">
            {/* Terms */}
            <div>
              <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Payment terms</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {TERMS_OPTIONS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTerms(t)}
                    className={`sans text-xs tracking-widest uppercase py-2.5 border transition-colors ${terms === t ? 'border-black bg-neutral-900 text-white' : 'border-neutral-200 text-neutral-500 hover:border-neutral-500'}`}
                    data-testid={`invoice-terms-${t.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Remit methods */}
            <div>
              <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Remit to</label>
              <div className="space-y-2">
                <RemitRow
                  id="zelle" label="Zelle"
                  checked={zelleOn} onCheck={setZelleOn}
                  detail={zelleDetail} onDetail={setZelleDetail}
                  placeholder="email@example.com"
                />
                <RemitRow
                  id="ach" label="ACH / Wire"
                  checked={achOn} onCheck={setAchOn}
                  detail={achDetail} onDetail={setAchDetail}
                  placeholder="Bank name · Routing · Account (last 4)"
                  multiline
                />
                <RemitRow
                  id="check" label="Check"
                  checked={checkOn} onCheck={setCheckOn}
                  detail={checkDetail} onDetail={setCheckDetail}
                  placeholder="Make payable to IJE HAUS LLC · Mailing address"
                  multiline
                />
              </div>
            </div>

            {/* Adjustments */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <NumberField label="Tax rate (%)" value={taxRate} onChange={setTaxRate} testid="invoice-tax" min={0} step="0.01" />
              <NumberField label="Shipping ($)" value={shipping} onChange={setShipping} testid="invoice-shipping" min={0} step="0.01" />
              <NumberField label="Discount ($)" value={discount} onChange={setDiscount} testid="invoice-discount" min={0} step="0.01" />
            </div>

            {/* Preview */}
            <div className="border border-neutral-200 bg-neutral-50 p-3">
              <p className="sans text-[10px] tracking-widest uppercase text-neutral-500 mb-2">Preview</p>
              <div className="space-y-1 text-sm">
                <Row label="Subtotal" value={fmt(preview.sub)} />
                {preview.taxAmt > 0 && <Row label={`Tax (${Number(taxRate) || 0}%)`} value={fmt(preview.taxAmt)} />}
                {preview.ship > 0 && <Row label="Shipping" value={fmt(preview.ship)} />}
                {preview.disc > 0 && <Row label="Discount" value={`− ${fmt(preview.disc)}`} />}
                <div className="flex items-center justify-between pt-2 mt-1 border-t border-neutral-200">
                  <span className="sans text-xs tracking-widest uppercase text-neutral-500">Total due</span>
                  <span className="serif text-lg text-[color:var(--ije-pink-dark)]" data-testid="invoice-preview-total">{fmt(preview.total)}</span>
                </div>
              </div>
            </div>

            <div>
              <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="ije-input w-full p-3 resize-none"
                placeholder="Anything for the customer to know…"
                data-testid="invoice-notes"
              />
            </div>

            <label className="flex items-center gap-2 sans text-xs tracking-widest uppercase text-neutral-500 cursor-pointer">
              <input
                type="checkbox"
                checked={saveDefaults}
                onChange={(e) => setSaveDefaults(e.target.checked)}
                data-testid="invoice-save-defaults"
              />
              Save these remit + terms choices as defaults for next time
            </label>
          </div>
        )}

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black px-4 h-11 sm:h-10 w-full sm:w-auto"
            data-testid="invoice-cancel"
          >
            <X size={14} className="inline mr-1" /> Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || loading}
            className="btn-primary h-11 sm:h-10 w-full sm:w-auto"
            data-testid="invoice-create"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Create + download
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function RemitRow({ id, label, checked, onCheck, detail, onDetail, placeholder, multiline }) {
  return (
    <div className={`border p-3 transition-colors ${checked ? 'border-black' : 'border-neutral-200'}`}>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheck(e.target.checked)}
          data-testid={`invoice-remit-${id}-toggle`}
        />
        <span className="sans text-xs tracking-widest uppercase text-neutral-900">{label}</span>
      </label>
      {checked && (
        multiline ? (
          <textarea
            value={detail}
            onChange={(e) => onDetail(e.target.value)}
            rows={2}
            placeholder={placeholder}
            className="ije-input w-full p-2 mt-2 text-sm resize-none"
            data-testid={`invoice-remit-${id}-detail`}
          />
        ) : (
          <input
            value={detail}
            onChange={(e) => onDetail(e.target.value)}
            placeholder={placeholder}
            className="ije-input h-10 w-full mt-2"
            data-testid={`invoice-remit-${id}-detail`}
          />
        )
      )}
    </div>
  );
}

function NumberField({ label, value, onChange, testid, ...rest }) {
  return (
    <div>
      <label className="sans text-[10px] tracking-widest uppercase text-neutral-500 block mb-2">{label}</label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="ije-input h-10 w-full"
        data-testid={testid}
        {...rest}
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="sans text-xs text-neutral-600">{label}</span>
      <span className="serif text-neutral-900">{value}</span>
    </div>
  );
}
